import { supabase } from '../config/supabaseClient';
import { VideoRecording, VideoShare, VideoComment, ProcessingStatus, VideoUploadProgress, RecordingMetadata } from '../types/videoRecorder';
import { logger } from '../utils/logger';

/**
 * Service for managing video recordings with Supabase
 */
export const videoRecorderService = {
  /**
   * Upload a video file to Supabase storage
   */
  async uploadVideo(
    file: File, 
    title: string, 
    description: string | null = null,
    metadata: RecordingMetadata = {},
    progressCallback?: (progress: VideoUploadProgress) => void
  ): Promise<VideoRecording | null> {
    try {
      // Basic input validation
      if (!file) {
        throw new Error('A video file is required');
      }
      if (!title || !title.trim()) {
        throw new Error('A title is required');
      }
      if (!file.type || !file.type.startsWith('video/')) {
        throw new Error('Invalid file type. Please upload a video file.');
      }
      // Optional: 1GB size guard (adjust as needed)
      const MAX_BYTES = 1024 * 1024 * 1024;
      if (typeof file.size === 'number' && file.size > MAX_BYTES) {
        throw new Error('File too large. Maximum size is 1GB.');
      }

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Create file path for the video - store videos in user-specific folders for RLS
      const filePath = `${user.id}/${new Date().getTime()}_${file.name.replace(/\s+/g, '_')}`;
      
      if (progressCallback) {
        progressCallback({
          status: ProcessingStatus.UPLOADING,
          progress: 0,
          message: 'Starting upload...'
        });
      }

      // Upload video to storage bucket
      const { error: storageError } = await supabase.storage
        .from('videos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });
      
      if (storageError) {
        throw storageError;
      }
      
      if (progressCallback) {
        progressCallback({
          status: ProcessingStatus.UPLOADING,
          progress: 50,
          message: 'Upload complete, saving metadata...'
        });
      }

      // Create a database entry for the video
      const { data: recordingData, error: recordingError } = await supabase
        .from('video_recordings')
        .insert({
          user_id: user.id,
          title,
          description,
          storage_path: filePath,
          duration_seconds: metadata.duration || null,
          metadata
        })
        .select()
        .single();
      
      if (recordingError) {
        // If the database insert fails, try to clean up the uploaded file
        await supabase.storage.from('videos').remove([filePath]);
        throw recordingError;
      }
      
      if (progressCallback) {
        progressCallback({
          status: ProcessingStatus.COMPLETED,
          progress: 100,
          message: 'Video uploaded successfully',
          videoId: recordingData.id
        });
      }
      
      return recordingData;
    } catch (error) {
      logger.error('Error uploading video:', error);
      if (progressCallback) {
        progressCallback({
          status: ProcessingStatus.FAILED,
          progress: 0,
          message: 'Upload failed',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      return null;
    }
  },

  /**
   * Get a signed URL for a thumbnail path
   */
  async getThumbnailSignedUrl(storagePath: string, expiresIn = 3600): Promise<string | null> {
    try {
      if (!storagePath) return null;
      const { data, error } = await supabase.storage
        .from('thumbnails')
        .createSignedUrl(storagePath, expiresIn);
      if (error) throw error;
      return data?.signedUrl || null;
    } catch (error) {
      logger.error('Error getting thumbnail signed URL:', error);
      return null;
    }
  },

  /**
   * Generate a thumbnail from a video file
   */
  async generateThumbnail(videoFile: File, time = 1): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        // Validate input
        if (!videoFile || videoFile.size === 0) {
          logger.error('Invalid or empty video file for thumbnail');
          return resolve(null);
        }
        
        logger.debug('Generating thumbnail', { 
          fileName: videoFile.name, 
          fileSize: videoFile.size,
          fileType: videoFile.type,
          targetTime: time
        });
        
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        let url: string | null = null;
        let timeoutId: number | null = null;

        const cleanup = () => {
          if (timeoutId) window.clearTimeout(timeoutId);
          if (url) URL.revokeObjectURL(url);
          video.onloadedmetadata = null;
          video.onseeked = null;
          video.onerror = null;
        };

        video.onloadedmetadata = () => {
          try {
            const seekTarget = Math.min(Math.max(0.1, time), Math.max(0.1, video.duration / 2));
            video.currentTime = seekTarget;
          } catch (err) {
            logger.error('Error setting currentTime for thumbnail:', err);
            cleanup();
            resolve(null);
          }
        };

        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              cleanup();
              return resolve(null);
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/png');
            cleanup();
            resolve(dataUrl);
          } catch (err) {
            logger.error('Error creating thumbnail:', err);
            cleanup();
            resolve(null);
          }
        };

        video.onerror = (e) => {
          logger.error('Error loading video for thumbnail', e);
          cleanup();
          resolve(null);
        };

        // Fallback timeout in case onseeked never fires
        timeoutId = window.setTimeout(() => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              cleanup();
              return resolve(null);
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL('image/png');
            cleanup();
            resolve(dataUrl);
          } catch (err) {
            console.error('Thumbnail timeout fallback failed:', err);
            cleanup();
            resolve(null);
          }
        }, 5000);

        url = URL.createObjectURL(videoFile);
        video.src = url;
        video.muted = true;
        video.playsInline = true as any;
      } catch (err) {
        console.error('Error in thumbnail generation:', err);
        resolve(null);
      }
    });
  },

  /**
   * Upload a thumbnail for a video
   */
  async uploadThumbnail(videoId: string, thumbnailDataUrl: string): Promise<string | null> {
    try {
      // Convert data URL to blob
      const res = await fetch(thumbnailDataUrl);
      const blob = await res.blob();
      
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }
      
      // Create file path for the thumbnail
      const filePath = `${user.id}/${videoId}_thumbnail.png`;
      
      // Upload thumbnail to storage
      const { error } = await supabase.storage
        .from('thumbnails')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: 'image/png'
        });
      
      if (error) {
        console.error('Thumbnail upload error details:', {
          error,
          filePath,
          blobSize: blob.size,
          blobType: blob.type,
          userId: user.id,
          errorMessage: error.message
        });
        throw error;
      }
      
      // Update the video record with the thumbnail path
      const { error: updateError } = await supabase
        .from('video_recordings')
        .update({ thumbnail_path: filePath })
        .eq('id', videoId);
      
      if (updateError) {
        console.error('Database update error for thumbnail:', {
          updateError,
          videoId,
          filePath
        });
        throw updateError;
      }
      
      return filePath;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      return null;
    }
  },

  /**
   * Upload a thumbnail for a video from a remote image URL
   */
  async uploadThumbnailFromUrl(videoId: string, imageUrl: string): Promise<string | null> {
    try {
      if (!imageUrl || !imageUrl.trim()) throw new Error('Image URL is required');

      // Use backend proxy endpoint so the server fetches and stores securely
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('No auth token found');

      // Simple retry with exponential backoff for transient failures
      const maxAttempts = 3;
      let lastErr: unknown = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const resp = await fetch(`/api/video/${encodeURIComponent(videoId)}/thumbnail-from-url`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ imageUrl })
          });

          if (!resp.ok) {
            let detail = '';
            try { detail = await resp.text(); } catch {}
            throw new Error(`Backend thumbnail upload failed (${resp.status}) ${detail}`.trim());
          }

          const json: unknown = await resp.json();
          if (!json || typeof json !== 'object' || !('path' in json)) {
            throw new Error('Invalid response shape');
          }
          const path = (json as { path?: unknown }).path;
          if (typeof path !== 'string' || path.length === 0) {
            throw new Error('Invalid path in response');
          }
          return path;
        } catch (err) {
          lastErr = err;
          if (attempt < maxAttempts) {
            const delay = 300 * Math.pow(2, attempt - 1);
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
        }
      }
      throw lastErr instanceof Error ? lastErr : new Error('Thumbnail upload failed');
    } catch (error) {
      console.error('Error uploading thumbnail from URL:', error);
      return null;
    }
  },

  /**
   * Get all videos for the current user
   */
  async getUserVideos(): Promise<VideoRecording[]> {
    try {
      const { data, error } = await supabase
        .from('video_recordings')
        .select('*')
        // Include records where is_deleted is false OR null (treat null as not deleted)
        .or('is_deleted.is.false,is_deleted.is.null')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching user videos:', error);
      return [];
    }
  },

  /**
   * Get a single video by ID
   */
  async getVideoById(videoId: string): Promise<VideoRecording | null> {
    try {
      const { data, error } = await supabase
        .from('video_recordings')
        .select('*')
        .eq('id', videoId)
        .single();
      
      if (error) {
        throw error;
      }
      
      // Increment view count
      await this.incrementViewCount(videoId);
      
      return data;
    } catch (error) {
      console.error('Error fetching video:', error);
      return null;
    }
  },

  /**
   * Increment the view count for a video
   */
  async incrementViewCount(videoId: string): Promise<void> {
    try {
      await supabase.rpc('increment_video_view_count', {
        video_id: videoId
      });
    } catch (error: any) {
      const msg = String(error?.message || '');
      const code = String(error?.code || '');
      // If RPC is missing (404 or function not found), attempt direct update as a fallback
      if (code === '404' || /increment_video_view_count/i.test(msg)) {
        try {
          await supabase
            .from('video_recordings')
            .update({ view_count: (undefined as unknown) as number })
            .eq('id', videoId);
          // Note: PostgREST can't do atomic increment without RPC; this is a no-op placeholder to avoid error noise.
          // Prefer creating the RPC shown in docs to perform atomic increments.
        } catch (_) {
          // Swallow fallback errors to avoid noisy logs on public pages
        }
      } else {
        // Reduce noise; log minimally
        console.warn('View count increment skipped:', msg);
      }
    }
  },

  /**
   * Update a video's metadata
   */
  async updateVideo(
    videoId: string, 
    updates: Partial<Pick<VideoRecording, 'title' | 'description' | 'is_public'>>
  ): Promise<VideoRecording | null> {
    try {
      const { data, error } = await supabase
        .from('video_recordings')
        .update(updates)
        .eq('id', videoId)
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error updating video:', error);
      return null;
    }
  },

  /**
   * Soft delete a video
   */
  async deleteVideo(videoId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('video_recordings')
        .update({ is_deleted: true })
        .eq('id', videoId);
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting video:', error);
      return false;
    }
  },

  /**
   * Upload subtitle file (WebVTT) to 'subtitles' bucket and link to video
   */
  async uploadSubtitleFromFile(videoId: string, file: File): Promise<string | null> {
    try {
      if (!file || !file.name.toLowerCase().endsWith('.vtt')) {
        throw new Error('Please upload a .vtt subtitle file');
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      const filePath = `${user.id}/${videoId}.vtt`;
      const { error: uploadError } = await supabase.storage
        .from('subtitles')
        .upload(filePath, file, { upsert: true, cacheControl: '3600', contentType: 'text/vtt' });
      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('video_recordings')
        .update({ subtitle_path: filePath })
        .eq('id', videoId);
      if (updateError) throw updateError;

      return filePath;
    } catch (error) {
      console.error('Error uploading subtitle file:', error);
      return null;
    }
  },

  /**
   * Save subtitle text (WebVTT content) to storage and link it
   */
  async saveSubtitleFromText(videoId: string, vttText: string): Promise<string | null> {
    try {
      if (!vttText || !vttText.trim().startsWith('WEBVTT')) {
        throw new Error('Invalid VTT content. Must start with WEBVTT');
      }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      const filePath = `${user.id}/${videoId}.vtt`;
      const blob = new Blob([vttText], { type: 'text/vtt' });
      const { error: uploadError } = await supabase.storage
        .from('subtitles')
        .upload(filePath, blob, { upsert: true, cacheControl: '3600', contentType: 'text/vtt' });
      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('video_recordings')
        .update({ subtitle_path: filePath })
        .eq('id', videoId);
      if (updateError) throw updateError;

      return filePath;
    } catch (error) {
      console.error('Error saving subtitle text:', error);
      return null;
    }
  },

  /**
   * Get signed URL for a subtitle file
   */
  async getSubtitleSignedUrl(storagePath: string, expiresIn = 3600): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('subtitles')
        .createSignedUrl(storagePath, expiresIn);
      if (error) throw error;
      return data?.signedUrl || null;
    } catch (error) {
      console.error('Error getting subtitle signed URL:', error);
      return null;
    }
  },

  /**
   * Remove subtitle association and file
   */
  async deleteSubtitle(videoId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('video_recordings')
        .select('subtitle_path')
        .eq('id', videoId)
        .single();
      if (error) throw error;

      if (data?.subtitle_path) {
        await supabase.storage.from('subtitles').remove([data.subtitle_path]);
      }
      const { error: updateError } = await supabase
        .from('video_recordings')
        .update({ subtitle_path: null })
        .eq('id', videoId);
      if (updateError) throw updateError;
      return true;
    } catch (error) {
      console.error('Error deleting subtitle:', error);
      return false;
    }
  },

  /**
   * Hard delete a video and its file
   */
  async permanentlyDeleteVideo(videoId: string): Promise<boolean> {
    try {
      // First get the video to know the storage path
      const { data: video, error: fetchError } = await supabase
        .from('video_recordings')
        .select('storage_path, thumbnail_path, subtitle_path')
        .eq('id', videoId)
        .single();
      
      if (fetchError) {
        throw fetchError;
      }
      
      // Delete from storage
      if (video.storage_path) {
        await supabase.storage.from('videos').remove([video.storage_path]);
      }
      
      // Delete thumbnail if exists
      if (video.thumbnail_path) {
        await supabase.storage.from('thumbnails').remove([video.thumbnail_path]);
      }

      // Delete subtitle if exists
      if (video.subtitle_path) {
        await supabase.storage.from('subtitles').remove([video.subtitle_path]);
      }
      
      // Delete from database
      const { error: deleteError } = await supabase
        .from('video_recordings')
        .delete()
        .eq('id', videoId);
      
      if (deleteError) {
        throw deleteError;
      }
      
      return true;
    } catch (error) {
      console.error('Error permanently deleting video:', error);
      return false;
    }
  },

  /**
   * Share a video with another user
   */
  async shareVideo(
    videoId: string, 
    sharedWithEmail: string,
    accessType: 'view' | 'edit' = 'view',
    expiresAt: Date | null = null
  ): Promise<VideoShare | null> {
    try {
      const { data, error } = await supabase
        .from('video_shares')
        .insert({
          video_id: videoId,
          shared_with_email: sharedWithEmail,
          access_type: accessType,
          expires_at: expiresAt?.toISOString() || null
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error sharing video:', error);
      return null;
    }
  },

  /**
   * Get public URL for a video
   */
  async getVideoPublicUrl(storagePath: string): Promise<string | null> {
    try {
      const { data } = await supabase.storage.from('videos').getPublicUrl(storagePath);
      return data.publicUrl;
    } catch (error) {
      console.error('Error getting video URL:', error);
      return null;
    }
  },

  /**
   * Get a signed URL for private videos
   */
  async getVideoSignedUrl(storagePath: string, expiresIn = 3600, downloadFileName?: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('videos')
        .createSignedUrl(storagePath, expiresIn);
      
      if (error) {
        throw error;
      }
      let url = data?.signedUrl || null;
      if (url && downloadFileName) {
        const sep = url.includes('?') ? '&' : '?';
        url = `${url}${sep}download=${encodeURIComponent(downloadFileName)}`;
      }
      return url;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  },

  /**
   * Get all comments for a video
   */
  async getVideoComments(videoId: string): Promise<VideoComment[]> {
    try {
      const { data, error } = await supabase
        .from('video_comments')
        .select('*')
        .eq('video_id', videoId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  },

  /**
   * Add a comment to a video
   */
  async addComment(
    videoId: string,
    comment: string,
    timestampSeconds: number | null = null,
    parentCommentId: string | null = null
  ): Promise<VideoComment | null> {
    try {
      const { data, error } = await supabase
        .from('video_comments')
        .insert({
          video_id: videoId,
          comment,
          timestamp_seconds: timestampSeconds,
          parent_comment_id: parentCommentId
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      return null;
    }
  },

  /**
   * Get all shares for a video
   */
  async getVideoShares(videoId: string): Promise<VideoShare[]> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Query shares where the user is either the owner of the video or the recipient
      const { data, error } = await supabase
        .from('video_shares')
        .select('*')
        .eq('video_id', videoId);

      if (error) {
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error getting video shares:', error);
      return [];
    }
  },

  /**
   * Delete a share
   */
  async deleteShare(shareId: string): Promise<boolean> {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('No authenticated user found');
      }

      // Soft delete the share by setting deleted_at
      let { error } = await supabase
        .from('video_shares')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', shareId);

      if (error) {
        // If column does not exist (42703), fallback to hard delete
        const code = (error as any)?.code;
        if (code === '42703' || /column .*deleted_at.* does not exist/i.test((error as any)?.message || '')) {
          const { error: delErr } = await supabase
            .from('video_shares')
            .delete()
            .eq('id', shareId);
          if (delErr) throw delErr;
        } else {
          throw error;
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting share:', error);
      return false;
    }
  }
};

// Add a stored procedure for incrementing video view count
// This will need to be executed in Supabase SQL editor:
/*
CREATE OR REPLACE FUNCTION increment_video_view_count(video_id UUID)
RETURNS void AS $$
BEGIN
  UPDATE video_recordings
  SET view_count = view_count + 1
  WHERE id = video_id;
END;
$$ LANGUAGE plpgsql;
*/
